// Shell for every screen in the submission flow.
//
// Mobile (< 1024px): a single centred column — the 390px design.
// Desktop (>= 1024px): the Figma split screen (desktop section 5:2178) — a
// full-bleed hero photo across the left 48.7% and the SAME content column
// centred in the right panel, with the wordmark at its top-left. The column
// width is identical in both, which is why the flow needed a new shell rather
// than new components.
//
// Measured from frame 5:2179 (1512x877): hero 736w full height, column 378w
// starting at y=116, wordmark at x=801/y=35 (65px in from the panel edge).
//
// Above the reference frame the column steps up (see ./breakpoints) — 378px is
// correct at 1512px and stranded at 2560px, where the panel alone is ~1300px
// wide. Widths stay inside a readable measure for a single-column form; the
// list screens (`wide`) scale further because they lay out cards, not prose.

import React from "react";
import styled, { css } from "styled-components";
import SubmissionHeader from "./SubmissionHeader";
import { DESKTOP, DESKTOP_WIDE, DESKTOP_XL, DESKTOP_XXL } from "./breakpoints";
import heroImage from "../../../images/submission-hero.jpg";
import { useLoadFont } from "../../../helpers/hooks";

interface Props {
  // When set, the blue step header renders at the top of the column.
  step?: number;
  // Entry/list screens (casting calls) lay out cards rather than prose, so they
  // take a much wider measure at every desktop step.
  wide?: boolean;
  children?: React.ReactNode;
}

// The one webfont the design uses. Same loader and URL family the 2025
// selling-features pages use, so a visitor coming from them hits the cache.
const OPEN_SANS =
  "https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700;800&display=swap";

const SubmissionLayout: React.FC<Props> = ({ step, wide, children }) => {
  useLoadFont(OPEN_SANS);
  return (
  <Shell>
    <Hero aria-hidden="true" />
    <Panel>
      {/* The official lockup (src/images, copied to /images at build) rather
          than the wordmark re-typed in the page font: it is the brand, and a
          re-typed version drifts from it the moment the font changes. */}
      <Brand href={process.env.REACT_APP_CWB_Site}>
        <BrandLogo src="/images/icon-twb-logo-full.svg" alt="theWorkbook" />
      </Brand>
      <Column wide={wide}>
        {step ? <SubmissionHeader step={step} /> : null}
        {children}
      </Column>
    </Panel>
  </Shell>
  );
};

const Shell = styled.div`
  display: flex;
  font-family: "Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  color: #0a1f44;
  width: 100%;
  /* The full viewport at every width now: the 60px it used to subtract was
     the global header bar, which the flow no longer renders on any screen
     size. Leaving it in drew a grey band under every short page. */
  min-height: 100vh;
  background-color: #ffffff;
`;

const Hero = styled.div`
  display: none;

  @media all and (min-width: ${DESKTOP}) {
    display: block;
    flex: 0 0 48.7%;
    align-self: stretch;
    /* Declared only inside the media query so phones never fetch the asset. */
    background-image: url(${heroImage});
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;
    background-color: #a9dcf3;
  }
`;

const Panel = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media all and (min-width: ${DESKTOP}) {
    align-items: stretch;
    padding: 35px 0 48px;
    overflow-y: auto;
    max-height: 100vh;
  }
`;

// Figma shows the logo lockup top-left on every screen, mobile included. The
// mobile version used to lean on the app's global header bar, which draws the
// icon alone; the flow now draws its own at every width, lined up with the
// content column below it.
const Brand = styled.a`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 420px;
  padding: 20px 16px 6px;
  text-decoration: none;
  flex: 0 0 auto;

  @media all and (min-width: ${DESKTOP}) {
    width: auto;
    max-width: none;
    padding: 0;
    margin: 0 0 34px 65px;
  }
`;

// 149x40 viewBox; 40px tall keeps the icon at the design's 44px-ish square.
const BrandLogo = styled.img`
  display: block;
  height: 40px;
  width: auto;
`;

const Column: any = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 8px 16px 32px;

  @media all and (min-width: ${DESKTOP}) {
    /* The Figma measurement, exact up to the 1512px reference frame. */
    width: 378px;
    max-width: 378px;
    padding: 0 18px 40px;
    margin: 0 auto;
    flex: 0 0 auto;
  }

  @media all and (min-width: ${DESKTOP_WIDE}) {
    width: 460px;
    max-width: 460px;
    padding-bottom: 48px;
  }

  @media all and (min-width: ${DESKTOP_XL}) {
    width: 520px;
    max-width: 520px;
  }

  @media all and (min-width: ${DESKTOP_XXL}) {
    width: 600px;
    max-width: 600px;
  }

  /* Must come after the base steps: same specificity, so source order decides.
     styled-components v3 hashes the evaluated css, which means wide and narrow
     columns get different class names — fine here, there are no sibling
     selectors on this component. */
  ${(p: any) =>
    p.wide &&
    css`
      @media all and (min-width: ${DESKTOP}) {
        width: 100%;
        max-width: 640px;
      }

      @media all and (min-width: ${DESKTOP_WIDE}) {
        max-width: 860px;
      }

      @media all and (min-width: ${DESKTOP_XL}) {
        max-width: 1000px;
      }

      @media all and (min-width: ${DESKTOP_XXL}) {
        max-width: 1160px;
      }
    `}
`;

export default SubmissionLayout;
